'use client'

import { CalendarSearchIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { forwardRef, memo, useEffect, useRef, useState } from 'react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { MarketStatusNotice } from '@/components/widget/market-status-notice'
import Timer from '@/components/widget/timer'
import { ChartGoToPanel } from '@/features/chart/components/widgets/chart-go-to-panel'
import ChartScaleControls from '@/features/chart/components/widgets/chart-scale-controls'
import IntervalSelector from '@/features/chart/components/widgets/interval-selector'
import { CHART_RANGE_PRESETS, type ChartRangePreset } from '@/features/chart/lib/utils/chart-util'
import { useWidgetConfigSync } from '@/features/dashboard/hooks/use-widget-config-sync'
import { useLinkingGroupState } from '@/hooks/use-linking-group-state'
import { cn } from '@/lib/utils'
import { Exchange } from '@/types/generated/common/types'

const TIMEFRAME_BUTTONS_MIN_WIDTH = 700
const MOBILE_UI_THRESHOLD = 550

interface ChartToolbarProps {
  cardId: string
  exchange: Exchange
  timeframe: number
  activeRangeLabel: string | null
  onTimeframeChange: (timeframe: number) => void
  onRangePresetChange: (preset: ChartRangePreset) => void
  /** Go to: a moment to frame around, or an exact window (see ChartGoToPanel). */
  onGoToTime: (at: Date) => void
  onGoToRange: (from: Date, to: Date) => void
}

/**
 * The range presets (5y … 1d): each sets a candle interval AND a "last N up to now" window.
 * `compact` fits a 21px chart footer (the Percept chart's); the default is the TV toolbar's.
 */
export function ChartRangePresetButtons({
  activeLabel,
  onSelect,
  compact = false,
}: {
  activeLabel: string | null
  onSelect: (preset: ChartRangePreset) => void
  compact?: boolean
}) {
  const t = useTranslations('trade.chartToolbar')
  return (
    <div className={cn('text-foreground/60 flex', compact ? 'items-center' : 'items-end')}>
      {CHART_RANGE_PRESETS.map((preset, index) => (
        <button
          key={preset.label}
          type="button"
          aria-pressed={activeLabel === preset.label}
          className={cn(
            'flex cursor-pointer items-center justify-center text-center font-sans tracking-tight transition-colors',
            compact ? 'h-[17px] px-1 text-[11px]' : 'h-7 px-1.5 text-xs',
            index === 0 && 'relative z-20',
            activeLabel === preset.label
              ? compact
                ? 'text-foreground font-medium'
                : 'text-foreground border-b-foreground h-6.5 border-b-2'
              : 'hover:text-foreground/80'
          )}
          onClick={() => onSelect(preset)}
        >
          {t(`rangePresets.${preset.label}`)}
        </button>
      ))}
    </div>
  )
}

/** The calendar button that opens "go to date" (TV's own dialog, or the Percept popover). */
export const GoToDateButton = forwardRef<
  HTMLButtonElement,
  { compact?: boolean } & React.ComponentPropsWithoutRef<'button'>
>(function GoToDateButton({ compact = false, className, ...props }, ref) {
  const t = useTranslations('trade.chartToolbar')
  return (
    <button
      ref={ref}
      type="button"
      title={t('goToDate')}
      aria-label={t('goToDate')}
      className={cn(
        'text-foreground/60 hover:text-foreground/80 data-[state=open]:text-foreground flex cursor-pointer items-center justify-center transition-colors',
        compact ? 'size-[17px]' : 'size-7',
        className
      )}
      {...props}
    >
      <CalendarSearchIcon className={compact ? 'size-3' : 'size-3.5'} />
    </button>
  )
})

function ChartToolbar({
  cardId,
  exchange,
  timeframe,
  activeRangeLabel,
  onTimeframeChange,
  onRangePresetChange,
  onGoToTime,
  onGoToRange,
}: ChartToolbarProps) {
  const [goToOpen, setGoToOpen] = useState(false)
  const t = useTranslations('trade.chartToolbar')
  const { chartOption } = useLinkingGroupState(cardId)
  const { syncConfig } = useWidgetConfigSync()
  const selectedChartType = chartOption?.chartType ?? 1

  const containerRef = useRef<HTMLDivElement>(null)
  const [showTimeframeButtons, setShowTimeframeButtons] = useState(true)
  const [isMobileUI, setIsMobileUI] = useState(false)

  const onClickRangePreset = (preset: ChartRangePreset) => {
    onRangePresetChange(preset)
    syncConfig(cardId, {
      timeframe: preset.timeframeSeconds.toString(),
      chartType: selectedChartType.toString(),
    })
  }

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => {
      setShowTimeframeButtons(entry.contentRect.width >= TIMEFRAME_BUTTONS_MIN_WIDTH)
      setIsMobileUI(entry.contentRect.width <= MOBILE_UI_THRESHOLD)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={containerRef} className="flex w-full items-center gap-1 px-2 py-1.5">
      <div className="flex items-center gap-1 pl-1">
        {showTimeframeButtons ? (
          <ChartRangePresetButtons activeLabel={activeRangeLabel} onSelect={onClickRangePreset} />
        ) : (
          <IntervalSelector
            timeframe={timeframe}
            onTimeframeChange={onTimeframeChange}
            hideLabel={isMobileUI}
          />
        )}
      </div>

      {showTimeframeButtons && (
        <>
          <span className="h-4 w-0 shrink-0 border-l border-white/20" />
          <Popover open={goToOpen} onOpenChange={setGoToOpen}>
            <PopoverTrigger asChild>
              <GoToDateButton />
            </PopoverTrigger>
            <PopoverContent side="top" align="start" className="no-drag w-auto overflow-hidden p-0">
              <ChartGoToPanel
                onGoToDate={(at) => {
                  setGoToOpen(false)
                  onGoToTime(at)
                }}
                onGoToRange={(from, to) => {
                  setGoToOpen(false)
                  onGoToRange(from, to)
                }}
                onCancel={() => setGoToOpen(false)}
              />
            </PopoverContent>
          </Popover>
        </>
      )}

      {/* `ml-auto` lives here, not on MarketStatusNotice itself — that component
          returns null for an always-open/normally-open market (see its own
          `isAlwaysOpen`/OPEN-status early returns), which would otherwise drop
          the margin from the DOM entirely and stop pushing Timer/scale
          controls to the right. */}
      <div className="ml-auto flex min-w-0 shrink-0 items-center">
        <MarketStatusNotice
          exchange={exchange}
          className="max-sm:max-w-6 max-sm:px-1.5 max-sm:[&>span]:sr-only"
        />
      </div>

      <div className="mr-2 mb-1 shrink-0">
        <Timer />
      </div>

      <span className="mr-1 h-4 w-0 shrink-0 border-l border-white/20" />

      <ChartScaleControls
        cardId={cardId}
        isMobileUI={isMobileUI}
        syncExtra={{ timeframe: timeframe.toString(), chartType: selectedChartType.toString() }}
      />
    </div>
  )
}

// Props are stable across the parent's live-price re-renders; memo keeps this
// subtree out of the per-tick render path.
export default memo(ChartToolbar)
